import { useMemo, type RefObject } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useCollectionBinding, useSlotGroup, useGallery, useProperty, RichTextContent } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';
import {
  normalizeBedroomDescription,
  resolveBedroomOptionsLabel,
} from '../utils/bedrooms';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  subtitle: string;
  param: string;
  count: number;
  priceRange: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

function keyToShortLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bed',
    two: '2 Bed',
    three: '3 Bed',
    four: '4 Bed',
    five: '5 Bed',
  };
  return labels[key] || `${key} Bed`;
}

function formatPriceRange(plans: Floorplan[]): string {
  const prices = plans
    .flatMap((p) => [p.floorplan_price_min, p.floorplan_price_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!prices.length) return 'Call for Pricing';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `From $${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const plans = groups[key];
    const count = plans.reduce((sum, p) => {
      const units = p.floorplan_available_units ? parseInt(String(p.floorplan_available_units), 10) : 0;
      return sum + (isNaN(units) ? 0 : units);
    }, 0);
    return {
      key,
      label: keyToLabel(key),
      subtitle: keyToShortLabel(key),
      param: key,
      count,
      priceRange: formatPriceRange(plans),
    };
  });
}

// ============================================================================
// Interior photo selection from gallery
// ============================================================================

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'unit', 'bedroom', 'living room', 'living',
  'kitchen', 'bathroom', 'bath', 'inside', 'home', 'den', 'dining',
  'closet', 'counter', 'cabinet', 'window', 'floor', 'carpet',
  'hardwood', 'granite', 'stainless', 'appliance', 'washer', 'dryer',
];

function scoreInterior(photo: PhotoGallery): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

/**
 * Strips query params and trailing slashes so CDN variants of
 * the same image (e.g. ?width=400 vs ?width=800) are treated as one.
 */
function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

function pickInteriorPhotos(gallery: PhotoGallery[], count: number): string[] {
  if (!gallery.length) return [];

  // Deduplicate the gallery by normalized URL first
  const seen = new Set<string>();
  const unique: { photo: PhotoGallery; score: number }[] = [];
  for (const photo of gallery) {
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push({ photo, score: scoreInterior(photo) });
  }

  // Sort by score descending
  unique.sort((a, b) => b.score - a.score);

  // Take the top-scored unique photos, then spread them out by
  // distributing evenly across the gallery order to avoid visually
  // similar adjacent photos (e.g. 3 kitchen shots in a row).
  const pool = unique.slice(0, Math.max(count * 2, 8));
  if (pool.length <= count) {
    return pool.map((p) => p.photo.image!);
  }

  // Pick every Nth item to space them out visually
  const step = pool.length / count;
  const picked: string[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.min(Math.floor(i * step), pool.length - 1);
    picked.push(pool[idx].photo.image!);
  }
  return picked;
}

// ============================================================================
// Card slot group definitions
// ============================================================================
// Each floor plan card has an editable image, heading, and sub_label slot so
// users can override the auto-selected gallery photo and overlay text via the
// content editor. We support up to 7 cards (1 "View All" + 6 bedroom categories).

const cardViewAllDef: SlotGroupDefinition = {
  label: 'View All Card',
  fields: {
    image: {
      label: 'Card Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt: 'An interior photo that represents living here overall.',
        context: 'Card thumb on the Home floor plans grid, View All card (first of up to 7). Do not reuse the bedroom-category cards.',
      },
    },
    heading: {
      label: 'Card Heading',
      type: 'richtext',
      defaultValue: '<p>ALL</p>',
    },
    sub_label: {
      label: 'Card Sub Label',
      type: 'richtext',
      defaultValue: '<p>View All Layouts</p>',
    },
  },
};

const card0Def: SlotGroupDefinition = {
  label: 'Card 1',
  fields: {
    image: {
      label: 'Card Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt: 'An unused interior photo of a typical home at this property.',
        context: 'Card thumb, bedroom-category card 1 of 6 after View All. Different photo from View All and cards 2–6.',
      },
    },
    heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
    sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
  },
};

const card1Def: SlotGroupDefinition = {
  label: 'Card 2',
  fields: {
    image: {
      label: 'Card Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt: 'Another unused interior photo that is also a real unit or living space here.',
        context: 'Card thumb, bedroom-category card 2 of 6 after View All. Different photo from View All and cards 1, 3–6.',
      },
    },
    heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
    sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
  },
};

const card2Def: SlotGroupDefinition = {
  label: 'Card 3',
  fields: {
    image: {
      label: 'Card Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt: 'A further unused interior photo from this property.',
        context: 'Card thumb, bedroom-category card 3 of 6 after View All. Different photo from View All and cards 1, 2, 4–6.',
      },
    },
    heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
    sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
  },
};

const card3Def: SlotGroupDefinition = {
  label: 'Card 4',
  fields: {
    image: {
      label: 'Card Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt: 'A remaining unused interior photo that still belongs to this property.',
        context: 'Card thumb, bedroom-category card 4 of 6 after View All. Different photo from View All and cards 1–3, 5, and 6.',
      },
    },
    heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
    sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
  },
};

const card4Def: SlotGroupDefinition = {
  label: 'Card 5',
  fields: {
    image: {
      label: 'Card Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt: 'An unused interior photo not already chosen for the other floor-plan cards.',
        context: 'Card thumb, bedroom-category card 5 of 6 after View All. Different photo from View All and cards 1–4 and 6.',
      },
    },
    heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
    sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
  },
};

const card5Def: SlotGroupDefinition = {
  label: 'Card 6',
  fields: {
    image: {
      label: 'Card Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt: 'A last unused interior photo from this property.',
        context: 'Card thumb, bedroom-category card 6 of 6 after View All. Different photo from View All and cards 1–5.',
      },
    },
    heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
    sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
  },
};

// ============================================================================
// Category Card Inner
// ============================================================================

function CategoryCardInner({
  cat,
  image,
  delay,
  headingHtml,
  subLabelHtml,
}: {
  cat: BedroomCategory;
  image: string;
  delay: number;
  headingHtml?: string | null;
  subLabelHtml?: string | null;
}) {
  const computedHeading = cat.key === 'studio' ? 'S' : BED_ORDER.indexOf(cat.key).toString();
  const computedSubLabel = `${cat.label} Layouts`;

  return (
    <a
      data-reveal
      href={`/floor-plans?bedroom=${cat.param}`}
      className="reveal-fade-up fp-card glow-trace-wrap group relative block aspect-[4/3] sm:aspect-[3/4] overflow-hidden cursor-pointer"
    >
      <GlowTrace delay={delay} />
      {image && (
        <VibesitesImage
          url={image}
          alt={`${cat.label} floor plans`}
          sizes="(min-width: 1280px) 16vw, (min-width: 1024px) 20vw, (min-width: 640px) 50vw, 100vw"
          className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
        />
      )}
      <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-[var(--vibe-color-text-on-primary)] px-4">
        {headingHtml ? (
          <div
            className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg"
            dangerouslySetInnerHTML={{ __html: headingHtml }}
          />
        ) : (
          <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">
            {computedHeading}
          </span>
        )}
        {subLabelHtml ? (
          <div
            className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md"
            dangerouslySetInnerHTML={{ __html: subLabelHtml }}
          />
        ) : (
          <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">
            {computedSubLabel}
          </span>
        )}
      </div>
    </a>
  );
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const property = useProperty();
  const label = useContentToken('floorplanslist.label');

  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const formattedLabel = useMemo(() => {
    // Prefer live floor-plan counts; fall back to the token / property range.
    const source = (label || property.property_bedrooms_range || '') as string;
    const plans = floorplans?.length ? floorplans : property.floorplans;
    const listed = resolveBedroomOptionsLabel(plans, source);
    if (listed) return `${listed} Layouts`;
    if (!source.trim()) return null;
    const normalized = normalizeBedroomDescription(source, null).trim();
    return normalized ? `${normalized} Layouts` : null;
  }, [floorplans, label, property.floorplans, property.property_bedrooms_range]);

  const gallery = useGallery();

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  // +1 for the "View All" card
  const photos = useMemo(
    () => pickInteriorPhotos(gallery, categories.length + 1),
    [gallery, categories.length],
  );

  // Resolve card slot groups (hooks must be called unconditionally)
  const viewAllSlot = useSlotGroup('floorplanslist.card_viewall', cardViewAllDef);
  const cardSlot0 = useSlotGroup('floorplanslist.card_0', card0Def);
  const cardSlot1 = useSlotGroup('floorplanslist.card_1', card1Def);
  const cardSlot2 = useSlotGroup('floorplanslist.card_2', card2Def);
  const cardSlot3 = useSlotGroup('floorplanslist.card_3', card3Def);
  const cardSlot4 = useSlotGroup('floorplanslist.card_4', card4Def);
  const cardSlot5 = useSlotGroup('floorplanslist.card_5', card5Def);
  const cardSlots = [cardSlot0, cardSlot1, cardSlot2, cardSlot3, cardSlot4, cardSlot5];

  // Use overridden image if set, otherwise fall back to auto-picked gallery photo
  const viewAllImage = viewAllSlot?.image || photos[0] || '';
  const cardImages = categories.map((_, i) => cardSlots[i]?.image || photos[i + 1] || '');

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const hasCategories = categories.length > 0;
  const totalCards = categories.length + 1;

  const gridCols = useMemo(() => {
    if (totalCards <= 2) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2';
    if (totalCards === 3) return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3';
    if (totalCards === 4) return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4';
    if (totalCards === 5) return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5';
    return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6';
  }, [totalCards]);

  return (
    <section
      className="py-16 md:py-24"
      style={{ background: 'color-mix(in srgb, var(--vibe-color-primary) 78%, white)' }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div ref={headingRef as RefObject<HTMLDivElement>} className="reveal-fade-up text-center mb-12">
          {formattedLabel && (
            <EditableToken path="floorplanslist.label" defaultField="property_bedrooms_range" as="p" className="font-body text-base sm:text-lg text-[var(--vibe-color-text-on-primary)] mb-3">
              {formattedLabel}
            </EditableToken>
          )}
          <RichTextContent path="floorplanslist.title" label="Title" defaultValue="<p>Make Yourself at Home</p>" as="h2" className="font-heading text-3xl sm:text-5xl font-bold text-[var(--vibe-color-text-on-primary)] mb-4 tracking-tight" />
          <RichTextContent path="floorplanslist.subtitle" label="Subtitle" defaultValue="<p>Please select your desired layout below.</p>" as="p" className="font-body text-base sm:text-lg text-[var(--vibe-color-text-on-primary)] max-w-2xl mx-auto" />
        </div>

        {hasCategories ? (
          <div ref={gridRef as RefObject<HTMLDivElement>} className={`grid ${gridCols} gap-3 md:gap-4`}>
            {/* "View All" card first — editable image + richtext heading/sub_label */}
            <EditableSlotGroup groupPath="floorplanslist.card_viewall" definition={cardViewAllDef}>
              <a
                data-reveal
                href="/floor-plans"
                className="reveal-fade-up fp-card glow-trace-wrap group relative block aspect-[4/3] sm:aspect-[3/4] overflow-hidden cursor-pointer"
              >
                <GlowTrace delay={0.3} />
                {viewAllImage && (
                  <VibesitesImage
                    url={viewAllImage}
                    alt="View all floor plans"
                    sizes="(min-width: 1280px) 16vw, (min-width: 1024px) 20vw, (min-width: 640px) 50vw, 100vw"
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                )}
                <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-[var(--vibe-color-text-on-primary)] px-4">
                  <div
                    className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg"
                    dangerouslySetInnerHTML={{ __html: viewAllSlot?.heading || '<p>ALL</p>' }}
                  />
                  <div
                    className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md"
                    dangerouslySetInnerHTML={{ __html: viewAllSlot?.sub_label || '<p>View All Layouts</p>' }}
                  />
                </div>
              </a>
            </EditableSlotGroup>

            {/* Bedroom category cards — each with editable image + richtext overlay (explicit paths for linter) */}
            {categories[0] && <EditableSlotGroup groupPath="floorplanslist.card_0" definition={card0Def}><CategoryCardInner cat={categories[0]} image={cardImages[0]} delay={0.55} headingHtml={cardSlots[0]?.heading || null} subLabelHtml={cardSlots[0]?.sub_label || null} /></EditableSlotGroup>}
            {categories[1] && <EditableSlotGroup groupPath="floorplanslist.card_1" definition={card1Def}><CategoryCardInner cat={categories[1]} image={cardImages[1]} delay={0.80} headingHtml={cardSlots[1]?.heading || null} subLabelHtml={cardSlots[1]?.sub_label || null} /></EditableSlotGroup>}
            {categories[2] && <EditableSlotGroup groupPath="floorplanslist.card_2" definition={card2Def}><CategoryCardInner cat={categories[2]} image={cardImages[2]} delay={1.05} headingHtml={cardSlots[2]?.heading || null} subLabelHtml={cardSlots[2]?.sub_label || null} /></EditableSlotGroup>}
            {categories[3] && <EditableSlotGroup groupPath="floorplanslist.card_3" definition={card3Def}><CategoryCardInner cat={categories[3]} image={cardImages[3]} delay={1.30} headingHtml={cardSlots[3]?.heading || null} subLabelHtml={cardSlots[3]?.sub_label || null} /></EditableSlotGroup>}
            {categories[4] && <EditableSlotGroup groupPath="floorplanslist.card_4" definition={card4Def}><CategoryCardInner cat={categories[4]} image={cardImages[4]} delay={1.55} headingHtml={cardSlots[4]?.heading || null} subLabelHtml={cardSlots[4]?.sub_label || null} /></EditableSlotGroup>}
            {categories[5] && <EditableSlotGroup groupPath="floorplanslist.card_5" definition={card5Def}><CategoryCardInner cat={categories[5]} image={cardImages[5]} delay={1.80} headingHtml={cardSlots[5]?.heading || null} subLabelHtml={cardSlots[5]?.sub_label || null} /></EditableSlotGroup>}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-on-primary)]/60">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .fp-card {
          border-radius: 4px;
        }
        .fp-card-overlay {
          background: linear-gradient(
            180deg,
            color-mix(in srgb, var(--vibe-color-primary) 70%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 85%, black) 100%
          );
          opacity: 0.3;
        }
        .fp-card:hover .fp-card-overlay {
          opacity: 0.45;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.label': {
      label: 'Section Label',
      type: 'text',
      defaultField: 'property_bedrooms_range',
      description: 'Label above the title showing listed bedroom options (e.g., "1, 2, & 3 bedrooms Layouts").',
      suggestedFields: ['property_bedrooms_range'],
    },
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Make Yourself at Home</p>',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Please select your desired layout below.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.card_viewall': cardViewAllDef,
    'floorplanslist.card_0': card0Def,
    'floorplanslist.card_1': card1Def,
    'floorplanslist.card_2': card2Def,
    'floorplanslist.card_3': card3Def,
    'floorplanslist.card_4': card4Def,
    'floorplanslist.card_5': card5Def,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorplansList;
